import {ZadCustomer, ZadCustomerType, ZadCustomerWritePayload, ZadMaarefatiLookup} from '../../../models/zad'
import {resolveZadUserMessage, ZadApiError} from './errors'

export const DEFAULT_ZAD_CUSTOMER_COUNTRY = 'Tunisie'
export const ZAD_MAAREFATI_SEARCH_MIN_CHARS = 2

export const ZAD_CUSTOMER_WRITE_KEYS = [
  'type',
  'firstName',
  'lastName',
  'displayName',
  'phone',
  'secondaryPhone',
  'email',
  'address',
  'city',
  'country',
  'taxId',
  'maarefatiStudent',
  'maarefatiParent',
  'notes',
] as const

export function preserveZadCustomerString(value: unknown): string {
  if (value == null) return ''
  return String(value)
}

export function displayZadOptional(value: unknown): string {
  const text = preserveZadCustomerString(value).trim()
  return text || '—'
}

export function shouldSearchZadMaarefati(search: string): boolean {
  return String(search || '').trim().length >= ZAD_MAAREFATI_SEARCH_MIN_CHARS
}

export function getZadMaarefatiId(ref: ZadMaarefatiLookup | string | null | undefined): string {
  if (!ref) return ''
  if (typeof ref === 'string') return ref
  return ref.id || ref._id || ''
}

export function toZadMaarefatiLookup(
  ref: ZadMaarefatiLookup | string | null | undefined
): ZadMaarefatiLookup | null {
  if (!ref || typeof ref === 'string') return null
  const id = ref.id || ref._id
  if (!id) return null
  return {
    id: String(id),
    firstName: ref.firstName || '',
    lastName: ref.lastName || '',
    role: ref.role != null ? String(ref.role) : '',
    classroomLabel: ref.classroomLabel,
    identifiantStudent: ref.identifiantStudent,
    phone: ref.phone,
  }
}

export function formatZadMaarefatiName(ref: ZadMaarefatiLookup | string | null | undefined): string {
  const lookup = toZadMaarefatiLookup(ref)
  if (!lookup) return '—'
  const name = [lookup.firstName, lookup.lastName].filter(Boolean).join(' ').trim()
  return name || '—'
}

export function getZadCustomerDisplayName(customer?: Partial<ZadCustomer> | null): string {
  if (!customer) return '—'
  if (customer.type === 'ORGANIZATION') {
    return displayZadOptional(customer.displayName)
  }
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim()
  return name || '—'
}

export function getZadCustomerTypeI18n(type?: ZadCustomerType | string): string {
  return type === 'ORGANIZATION'
    ? 'ZAD.CUSTOMERS.TYPE_ORGANIZATION'
    : 'ZAD.CUSTOMERS.TYPE_INDIVIDUAL'
}

export function getZadCustomerLinkKind(
  customer?: Pick<ZadCustomer, 'maarefatiStudent' | 'maarefatiParent'> | null
): 'student' | 'parent' | 'both' | 'none' {
  const hasStudent = Boolean(getZadMaarefatiId(customer?.maarefatiStudent))
  const hasParent = Boolean(getZadMaarefatiId(customer?.maarefatiParent))
  if (hasStudent && hasParent) return 'both'
  if (hasStudent) return 'student'
  if (hasParent) return 'parent'
  return 'none'
}

export function getZadCustomerLinkI18n(
  customer?: Pick<ZadCustomer, 'maarefatiStudent' | 'maarefatiParent'> | null
): string {
  const kind = getZadCustomerLinkKind(customer)
  if (kind === 'student') return 'ZAD.CUSTOMERS.STUDENT'
  if (kind === 'parent') return 'ZAD.CUSTOMERS.PARENT'
  if (kind === 'both') return 'ZAD.CUSTOMERS.LINK_BOTH'
  return 'ZAD.CUSTOMERS.NO_MAAREFATI_LINK'
}

export type ZadCustomerFormValues = {
  type: ZadCustomerType
  firstName: string
  lastName: string
  displayName: string
  phone: string
  secondaryPhone: string
  email: string
  address: string
  city: string
  country: string
  taxId: string
  notes: string
  maarefatiStudent: string
  maarefatiParent: string
  selectedStudent: ZadMaarefatiLookup | null
  selectedParent: ZadMaarefatiLookup | null
}

export function emptyZadCustomerFormValues(): ZadCustomerFormValues {
  return {
    type: 'INDIVIDUAL',
    firstName: '',
    lastName: '',
    displayName: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    address: '',
    city: '',
    country: DEFAULT_ZAD_CUSTOMER_COUNTRY,
    taxId: '',
    notes: '',
    maarefatiStudent: '',
    maarefatiParent: '',
    selectedStudent: null,
    selectedParent: null,
  }
}

export function toZadCustomerFormValues(customer?: ZadCustomer | null): ZadCustomerFormValues {
  if (!customer) return emptyZadCustomerFormValues()
  const selectedStudent = toZadMaarefatiLookup(customer.maarefatiStudent)
  const selectedParent = toZadMaarefatiLookup(customer.maarefatiParent)
  return {
    type: customer.type === 'ORGANIZATION' ? 'ORGANIZATION' : 'INDIVIDUAL',
    firstName: customer.firstName || '',
    lastName: customer.lastName || '',
    displayName: customer.displayName || '',
    phone: preserveZadCustomerString(customer.phone),
    secondaryPhone: preserveZadCustomerString(customer.secondaryPhone),
    email: customer.email || '',
    address: customer.address || '',
    city: customer.city || '',
    country: customer.country || DEFAULT_ZAD_CUSTOMER_COUNTRY,
    taxId: preserveZadCustomerString(customer.taxId),
    notes: customer.notes || '',
    maarefatiStudent: getZadMaarefatiId(customer.maarefatiStudent),
    maarefatiParent: getZadMaarefatiId(customer.maarefatiParent),
    selectedStudent,
    selectedParent,
  }
}

export function switchZadCustomerType(
  values: ZadCustomerFormValues,
  nextType: ZadCustomerType
): ZadCustomerFormValues {
  if (nextType === 'ORGANIZATION') {
    return {
      ...values,
      type: 'ORGANIZATION',
      firstName: '',
      lastName: '',
    }
  }
  return {
    ...values,
    type: 'INDIVIDUAL',
    displayName: '',
  }
}

export function validateZadCustomerForm(values: ZadCustomerFormValues): string | null {
  if (values.type === 'ORGANIZATION') {
    if (!String(values.displayName || '').trim()) return 'ZAD.CUSTOMERS.DISPLAY_NAME_REQUIRED'
    return null
  }
  if (!String(values.firstName || '').trim()) return 'ZAD.CUSTOMERS.FIRST_NAME_REQUIRED'
  if (!String(values.lastName || '').trim()) return 'ZAD.CUSTOMERS.LAST_NAME_REQUIRED'
  return null
}

function optionalLink(value: string | null | undefined): string | null {
  const text = preserveZadCustomerString(value).trim()
  return text || null
}

export function toZadCustomerPayload(values: ZadCustomerFormValues): ZadCustomerWritePayload {
  const type: ZadCustomerType = values.type === 'ORGANIZATION' ? 'ORGANIZATION' : 'INDIVIDUAL'
  const payload: ZadCustomerWritePayload = {
    type,
    phone: preserveZadCustomerString(values.phone).trim(),
    secondaryPhone: preserveZadCustomerString(values.secondaryPhone).trim(),
    email: values.email != null ? String(values.email).trim() : '',
    address: values.address != null ? String(values.address).trim() : '',
    city: values.city != null ? String(values.city).trim() : '',
    country:
      values.country != null && String(values.country).trim()
        ? String(values.country).trim()
        : DEFAULT_ZAD_CUSTOMER_COUNTRY,
    taxId: preserveZadCustomerString(values.taxId).trim(),
    notes: values.notes != null ? String(values.notes).trim() : '',
    maarefatiStudent: optionalLink(values.maarefatiStudent),
    maarefatiParent: optionalLink(values.maarefatiParent),
  }

  if (type === 'INDIVIDUAL') {
    payload.firstName = String(values.firstName || '').trim()
    payload.lastName = String(values.lastName || '').trim()
  } else {
    payload.displayName = String(values.displayName || '').trim()
  }

  return payload
}

export function resolveZadCustomerMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  if (err.errorCode === 'CUSTOMER_NOT_FOUND' || err.errorCode === 'NOT_FOUND') {
    return {i18nId: 'ZAD.CUSTOMERS.NOT_FOUND', fallback: 'Customer was not found.'}
  }
  if (err.errorCode === 'INVALID_CUSTOMER_TYPE') {
    return {i18nId: 'ZAD.CUSTOMERS.INVALID_TYPE', fallback: 'Invalid customer type.'}
  }
  if (err.errorCode === 'INVALID_MAAREFATI_STUDENT') {
    return {i18nId: 'ZAD.CUSTOMERS.INVALID_STUDENT', fallback: 'Invalid Maarefati student.'}
  }
  if (err.errorCode === 'INVALID_MAAREFATI_PARENT') {
    return {i18nId: 'ZAD.CUSTOMERS.INVALID_PARENT', fallback: 'Invalid Maarefati parent.'}
  }
  if (err.errorCode === 'VALIDATION_ERROR') {
    return {i18nId: 'ZAD.CUSTOMERS.VALIDATION_ERROR', fallback: 'Customer data is invalid.'}
  }
  if (err.errorCode === 'ROLE_FORBIDDEN') {
    return {i18nId: 'ZAD.COMMON.FORBIDDEN', fallback: 'You do not have access to this resource.'}
  }
  return resolveZadUserMessage(err)
}
